import { ModelType, PhoneNumberType } from '../../../common/enums.js';
import {
  fieldCantBeEmptyMsg,
  invalidPhoneNumberMsg,
  invalidSIMCardNumberMsg,
  invalidValueMsg,
  simCardOneSimPerSubscriptionMsg,
  t,
} from '../../../common/i18n/index.js';
import {
  formatTimestampToDDMMYYYY,
  getNextAllowedDay,
  getNormalizedDateFromDateString,
  isAllowedTransferDay,
} from '../../../common/utils/dateUtils.js';
import { EMAIL_REGEX } from '../../../common/utils/emailUtils';
import { PHONE_NUMBER_REGEX } from '../../../common/utils/phoneNumberUtils.js';
import { getLuhnRemainder } from '../../../common/utils/simCardUtils.js';
import { SIM_CARD_REGEX } from '../../../common/utils/validationUtils.js';
import { OrderItemUnit } from '../../../generated/api/orderItemUnit.js';
import { SimType } from '../../../generated/api/simType.js';
import { isMobileM2MProduct } from '../../ShoppingBasket/shoppingBasketHelpers.js';
import type { OnlineModel } from '../../../generated/api/onlineModel.js';
import type { PlaceOrderItemUnit } from '../placeOrderTypes.js';

/**
 * Configuration fields that can be shown during subscription ordering
 */
export enum SubscriptionConfigField {
  SELECT_PHONE_NUMBER = 'SELECT_PHONE_NUMBER',
  NUMBER_PRIVACY = 'NUMBER_PRIVACY',
  SIMCARD = 'SIMCARD',
  M2M_SIMCARD = 'M2M_SIMCARD',
}

/**
 * Check if onlineModel represents a voice subscription (needs phone number selection)
 * Includes legacy/minute-based subscriptions like Yrityspaketti, Yritystupla, etc.
 */
export const isVoiceSubscription = (onlineModel: OnlineModel): boolean => {
  const voiceModelTypes: string[] = [
    ModelType.Voice,
    ModelType.VoiceSME,
    ModelType.VoicePerusliittyma4g,
    ModelType.VoiceYrityspuhe,
    ModelType.Yrityspaketti,
    ModelType.Yritystupla,
    ModelType.YrityspuheUusi,
  ];
  return voiceModelTypes.includes(onlineModel.onlineModelCode);
};

/**
 * Check if onlineModel represents a mobile broadband subscription
 */
export const isMobileBroadbandSubscription = (onlineModel: OnlineModel): boolean => {
  return onlineModel.onlineModelCode === ModelType.MobileBroadband;
};

/**
 * Check if this subscription product needs configuration (phone number, SIM card, etc.)
 * This is used to determine when to show SubscriptionConfigurationHandler
 */
export const needsSubscriptionConfiguration = (onlineModel: OnlineModel): boolean =>
  isVoiceSubscription(onlineModel) || isMobileBroadbandSubscription(onlineModel) || isMobileM2MProduct(onlineModel);

/**
 * Determine which configuration fields to show based on subscription type
 * Mirrors the logic from OrderSubscriptionConfiguration in old checkout
 */
export const getFieldsToShow = (onlineModel: OnlineModel): SubscriptionConfigField[] => {
  const fields: SubscriptionConfigField[] = [];

  if (isMobileBroadbandSubscription(onlineModel)) {
    // Mobile Broadband: show SIM card configuration
    fields.push(SubscriptionConfigField.SIMCARD);
  } else if (isMobileM2MProduct(onlineModel)) {
    // M2M: show M2M-specific SIM card options (different sizes)
    fields.push(SubscriptionConfigField.M2M_SIMCARD);
  } else if (isVoiceSubscription(onlineModel)) {
    // Voice: show phone number selection, number privacy, and SIM card
    fields.push(
      SubscriptionConfigField.SELECT_PHONE_NUMBER,
      SubscriptionConfigField.NUMBER_PRIVACY,
      SubscriptionConfigField.SIMCARD
    );
  }

  return fields;
};

/**
 * Form state interface for SubscriptionConfigurationHandler
 * This is the internal UI state that maps to/from OrderItemUnit fields
 */
export interface SubscriptionConfigFormState {
  // Phone number selection
  phoneNumberType: PhoneNumberType;
  newPhoneNumber?: string;
  existingPhoneNumber?: string;
  transferDate?: string; // DD.MM.YYYY format

  // Number owner (for port-in)
  differentDonor: boolean;
  donorEmail?: string;
  fixedTermContract: boolean;

  // Number privacy
  numberPublicity: boolean;

  // SIM card
  simType: SimType | 'ACTIVATE_EXISTING';
  simCardNumber?: string;
}

/**
 * Default form state for subscription configuration
 */
export const getDefaultSubscriptionConfigFormState = (
  onlineModel: OnlineModel,
  holidays: Date[]
): SubscriptionConfigFormState => ({
  phoneNumberType: PhoneNumberType.NEW,
  transferDate: formatTimestampToDDMMYYYY(getNextAllowedDay(holidays).getTime()),
  differentDonor: false,
  fixedTermContract: false,
  numberPublicity: true,
  simType: isMobileM2MProduct(onlineModel) ? SimType.PHYSICAL_MINI_MICRO : SimType.PHYSICAL,
});

// ============================================================================
// Validation Functions
// ============================================================================

/**
 * Validate phone number format
 */
export const validatePhoneNumberFormat = (phoneNumber: string): string | undefined => {
  if (!phoneNumber) {
    return t.VPVR(fieldCantBeEmptyMsg);
  }
  if (!PHONE_NUMBER_REGEX.test(phoneNumber)) {
    return t.YLCX(invalidPhoneNumberMsg);
  }
  return undefined;
};

/**
 * Validate SIM card number format (regex + Luhn checksum)
 */
export const validateSimCardNumberFormat = (simCardNumber: string): string | undefined => {
  if (!simCardNumber) {
    return t.VPVR(fieldCantBeEmptyMsg);
  }
  if (!simCardNumber.match(new RegExp(SIM_CARD_REGEX))) {
    return t.SDXB(invalidSIMCardNumberMsg);
  }
  if (getLuhnRemainder(simCardNumber) !== 0) {
    return t.SDXB(invalidSIMCardNumberMsg);
  }
  return undefined;
};

/**
 * Check for duplicate SIM cards across all order items
 */
export const validateSimCardNotDuplicate = (simCardNumber: string, allSimCardNumbers: string[]): string | undefined => {
  const duplicateCount = allSimCardNumbers.filter(n => n === simCardNumber).length;
  if (duplicateCount > 1) {
    return t.RFH9(simCardOneSimPerSubscriptionMsg);
  }
  return undefined;
};

/**
 * Validate donor email (required when differentDonor is true)
 */
export const validateDonorEmail = (email: string | undefined, differentDonor: boolean): string | undefined => {
  if (!differentDonor) {
    return undefined;
  }
  if (!email) {
    return t.VPVR(fieldCantBeEmptyMsg);
  }
  if (!EMAIL_REGEX.test(email)) {
    return t.DUPA(invalidValueMsg);
  }
  return undefined;
};

/**
 * Validate transfer date (must be allowed day - not weekend, not holiday)
 */
export const validateTransferDate = (dateStr: string | undefined, holidays: Date[]): string | undefined => {
  if (!dateStr) {
    return t.VPVR(fieldCantBeEmptyMsg);
  }
  try {
    const date = getNormalizedDateFromDateString(dateStr);
    if (!isAllowedTransferDay(date, holidays)) {
      return t.DUPA(invalidValueMsg);
    }
    return undefined;
  } catch {
    return t.DUPA(invalidValueMsg);
  }
};

// ============================================================================
// Form State Mapping Functions
// ============================================================================

/**
 * Map form state to OrderItemUnit fields for API submission
 */
export const mapFormStateToOrderItemUnit = (formState: SubscriptionConfigFormState): Partial<PlaceOrderItemUnit> => {
  const isPortIn = formState.phoneNumberType === PhoneNumberType.EXISTING;

  const getPortInDate = (): number | undefined => {
    if (!isPortIn || !formState.transferDate) {
      return undefined;
    }
    try {
      return getNormalizedDateFromDateString(formState.transferDate).getTime();
    } catch {
      return undefined;
    }
  };

  return {
    mobileNumber: isPortIn ? formState.existingPhoneNumber : formState.newPhoneNumber,
    numberPorting: isPortIn ? OrderItemUnit.NumberPortingEnum.PORT_IN : OrderItemUnit.NumberPortingEnum.NEW_NUMBER,
    portInDate: getPortInDate(),
    differentDonor: isPortIn ? formState.differentDonor : undefined,
    donorEmail: isPortIn && formState.differentDonor ? formState.donorEmail : undefined,
    fixedTermContract: isPortIn ? formState.fixedTermContract : undefined,
    numberPublicity: formState.numberPublicity,
    simType: formState.simType === 'ACTIVATE_EXISTING' ? SimType.PHYSICAL : (formState.simType as SimType),
    simCardNumber: formState.simType === 'ACTIVATE_EXISTING' ? formState.simCardNumber : undefined,
  };
};

/**
 * Map OrderItemUnit fields back to form state (for hydration on back navigation)
 */
export const mapOrderItemUnitToFormState = (
  orderItem: PlaceOrderItemUnit,
  defaultDate: Date
): SubscriptionConfigFormState => {
  const isPortIn = orderItem.numberPorting === OrderItemUnit.NumberPortingEnum.PORT_IN;

  return {
    phoneNumberType: isPortIn ? PhoneNumberType.EXISTING : PhoneNumberType.NEW,
    newPhoneNumber: !isPortIn ? orderItem.mobileNumber : undefined,
    existingPhoneNumber: isPortIn ? orderItem.mobileNumber : undefined,
    transferDate: orderItem.portInDate
      ? formatTimestampToDDMMYYYY(orderItem.portInDate)
      : formatTimestampToDDMMYYYY(defaultDate.getTime()),
    differentDonor: orderItem.differentDonor ?? false,
    donorEmail: orderItem.donorEmail,
    fixedTermContract: orderItem.fixedTermContract ?? false,
    numberPublicity: orderItem.numberPublicity ?? true,
    simType: orderItem.simCardNumber ? 'ACTIVATE_EXISTING' : (orderItem.simType ?? SimType.PHYSICAL),
    simCardNumber: orderItem.simCardNumber,
  };
};

/**
 * Check if an order item has subscription configuration data (for hydration detection)
 */
export const hasSubscriptionConfigData = (orderItem: PlaceOrderItemUnit | undefined): boolean => {
  if (!orderItem) {
    return false;
  }
  return !!(
    orderItem.mobileNumber ||
    orderItem.simType ||
    orderItem.simCardNumber ||
    orderItem.numberPorting ||
    orderItem.numberPublicity !== undefined
  );
};
